import {
  Alert,
  Anchor,
  Button,
  Card,
  Group,
  NativeSelect,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../api/client';
import {
  useClearOrgParent,
  useOrgChildren,
  useOrgs,
  useSetOrgParent,
  type OrgDto,
} from '../api/orgs';

function errorDetail(err: unknown): string {
  if (err instanceof ApiError) {
    try {
      const parsed = JSON.parse(err.body) as { detail?: string; message?: string };
      return parsed.detail ?? parsed.message ?? `Request failed (${err.status})`;
    } catch {
      return `Request failed (${err.status})`;
    }
  }
  return 'Something went wrong';
}

export function OrgHierarchyCard({ org }: { org: OrgDto }) {
  const orgs = useOrgs();
  const children = useOrgChildren(org.id);
  const setParent = useSetOrgParent(org.id);
  const clearParent = useClearOrgParent(org.id);
  const [selected, setSelected] = useState('');

  const parentName = org.parentOrganisationId
    ? (orgs.data?.find((o) => o.id === org.parentOrganisationId)?.name ?? org.parentOrganisationId)
    : null;

  const childRows = children.data ?? [];
  const hasChildren = childRows.length > 0;

  const eligibleParents = useMemo(() => {
    if (!orgs.data) return [];
    return orgs.data.filter(
      (o) =>
        o.id !== org.id &&
        !o.deletedAt &&
        !o.parentOrganisationId &&
        (o.synthetic ?? false) === (org.synthetic ?? false),
    );
  }, [orgs.data, org.id, org.synthetic]);

  const mutationError = setParent.error ?? clearParent.error;

  return (
    <Card withBorder padding="lg" maw={680}>
      <Stack gap="sm">
        <Title order={5}>Organisation hierarchy</Title>

        {mutationError ? (
          <Alert color="red" title="Could not update hierarchy">
            {errorDetail(mutationError)}
          </Alert>
        ) : null}

        {org.parentOrganisationId ? (
          <Group justify="space-between">
            <Text>
              Parent:{' '}
              <Anchor component={Link} to={`/orgs/${org.parentOrganisationId}`}>
                {parentName}
              </Anchor>
            </Text>
            <Button
              variant="light"
              color="red"
              loading={clearParent.isPending}
              onClick={() => clearParent.mutate()}
            >
              Clear parent
            </Button>
          </Group>
        ) : hasChildren ? (
          <Text c="dimmed">
            This organisation has child sites, so it can’t also become a child.
          </Text>
        ) : (
          <Group align="flex-end">
            <NativeSelect
              label="Parent organisation"
              data={[
                { value: '', label: '— Select a parent —' },
                ...eligibleParents.map((o) => ({ value: o.id, label: o.name })),
              ]}
              value={selected}
              onChange={(e) => setSelected(e.currentTarget.value)}
              disabled={orgs.isLoading}
              style={{ flex: 1 }}
            />
            <Button
              loading={setParent.isPending}
              disabled={!selected}
              onClick={() => setParent.mutate(selected)}
            >
              Set parent
            </Button>
          </Group>
        )}

        {hasChildren ? (
          <Stack gap={4}>
            <Text fw={500} size="sm">
              Child organisations
            </Text>
            {childRows.map((c) => (
              <Anchor key={c.id} component={Link} to={`/orgs/${c.id}`}>
                {c.name}
              </Anchor>
            ))}
          </Stack>
        ) : null}
      </Stack>
    </Card>
  );
}
